import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps, getRendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import useStyle from "./CarrouselScaleItemRenderer.styles";
import useTranslatable from "../../../hooks/useTranslatable";
import useDimensionContext from "../../../hooks/useDimensionContext";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import useQuestion from "../../../hooks/useQuestion";
import { useMouseHover } from "../../../hooks/useMouseHover";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";
import { Theme } from "../../../types/Theme";
import { ButtonChoiceNavigation } from "../../controls/KeyboardNavigation/ButtonChoiceNavigation";
import { ChoiceContainer } from "../../../containers/controls/ChoiceContainer";
import { ChoiceRef } from "../../choices/BaseChoice";
import { Radio } from "../../choices/Radio";
import RadioRenderer from "./RadioRenderer";
import { VariableModel } from "../../../models/VariableModel";
import ChoiceOpenEndRenderer from "./ChoiceOpenEndRenderer";
import { getSettingNumber, getSettingString } from "../../../models/utilities/settingUtilities";
import { removeHtmlTags } from "../../../utils";
import { hasOpenEnd } from "../../../models/utilities/choiceUtilities";
import {
    getCarrouselScaleChoiceAriaLabelledBy,
    type CarrouselScaleAnchorLabelIds,
} from "./carrouselScaleA11y";

/** Hidden radio adapter: persists mentions via Radio without duplicating visible choice labels. */
class CarrouselScaleRadioAdapter extends Radio {
    renderChoice(choice: ChoiceModel) {
        const { isSelected, isDisabled, variable, choiceRef } = this.props;
        return (
            <RadioRenderer
                {...getRendererProps(this.props)}
                ref={choiceRef}
                variable={variable}
                choice={choice}
                name={variable.name}
                value={choice.value}
                onSelect={this.onSelect}
                checked={isSelected}
                disabled={isDisabled}
            />
        );
    }
}

interface Props extends RendererProps {
    choice: ChoiceModel;
    selected?: boolean;
    disabled?: boolean;
    variable: VariableModel;
    isFirst?: boolean;
    isLast?: boolean;
    openEnd?: string;
    onOpenEndChange?(choice: ChoiceModel, text: string): void;
    onOpenEndBlur?(choice: ChoiceModel): void;
    onAfterSelection?(): void;
    executeUpdateForIdAfterSelection?: string;
    choiceIndex?: number;
    choiceCount?: number;
    scaleAnchorLabelIds?: CarrouselScaleAnchorLabelIds;
    stackedLayout?: boolean;
    stretchRow?: boolean;
    cellWidth?: number;
    cellHeight?: number;
    minCellWidthPx?: number;
    contentPaddingX?: number;
    gridRendering?: boolean;
}

export default function CarrouselScaleItemRenderer(props: Props) {
    const {
        choice,
        selected,
        disabled,
        variable,
        isFirst,
        isLast,
        openEnd,
        onOpenEndChange,
        onOpenEndBlur,
        onAfterSelection,
        executeUpdateForIdAfterSelection,
        choiceIndex = 0,
        choiceCount = 1,
        scaleAnchorLabelIds,
        stackedLayout,
        stretchRow,
        cellWidth,
        cellHeight,
        minCellWidthPx: layoutMinCellWidthPx,
        contentPaddingX,
        gridRendering,
        language,
    } = props;

    const choiceRef: ChoiceRef = React.useRef();
    const theme = useTheme() as Theme;
    const useModernTheme = shouldUseModernTheme(theme);
    const dimension = useDimensionContext();
    const isMobileDimension = Boolean(dimension && dimension.maxWidth(480));
    const isLargeDisplay = Boolean(dimension && dimension.minWidth(1169));
    const useStackedCellLayout = Boolean(stackedLayout);
    const classes = useStyle({
        theme,
        selected,
        stackedLayout: useStackedCellLayout,
        stretchRow: !useStackedCellLayout && stretchRow,
        cellWidth: !useStackedCellLayout && !stretchRow ? cellWidth : undefined,
        cellHeight: !useStackedCellLayout ? cellHeight : undefined,
        isMobileDimension: !useStackedCellLayout && isMobileDimension,
        isLargeDisplay: !useStackedCellLayout && isLargeDisplay,
        minCellWidthPx: !useStackedCellLayout ? layoutMinCellWidthPx : undefined,
        contentPaddingX: useStackedCellLayout ? undefined : contentPaddingX,
    });
    const label = useTranslatable(choice, "Text");
    const [hover, pointerHandlers] = useMouseHover();
    const [focused, setFocused] = React.useState(false);

    const inputId = `${choice.elementId}_INPUT`;
    const ariaLabelledBy = getCarrouselScaleChoiceAriaLabelledBy(
        choice,
        choiceIndex,
        choiceCount,
        scaleAnchorLabelIds,
    );

    function onSelect() {
        if (disabled) {
            return;
        }
        if (choiceRef.current) {
            choiceRef.current.select();
            onAfterSelection?.();
        }
    }

    const { elementReference, onKeyDown } = useKeyboardNavigation(
        inputId,
        variable,
        useQuestion(),
        ButtonChoiceNavigation,
        onSelect,
    );

    const openEndSize = getSettingString(choice, "OpenEndSize", "Medium");
    const choiceLabel = removeHtmlTags(label);

    return (
        <div className={classes.item}>
            <div
                role="radio"
                id={inputId}
                ref={elementReference}
                aria-checked={selected}
                aria-disabled={disabled || undefined}
                {...(ariaLabelledBy ? { "aria-labelledby": ariaLabelledBy } : {})}
                tabIndex={disabled ? -1 : 0}
                className={classnames(
                    classes.choice,
                    useModernTheme && classes.choiceModernFocus,
                    useStackedCellLayout && classes.dynamicChoice,
                    !disabled && useModernTheme && hover && classes.choiceHover,
                    !disabled && !useModernTheme && (hover || focused) && classes.choiceActive,
                    selected && !(useModernTheme && disabled) && classes.choiceSelected,
                    useModernTheme && disabled && classes.choiceDisabled,
                )}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                {...(disabled ? {} : pointerHandlers)}
                onClick={disabled ? undefined : onSelect}
                onKeyDown={disabled ? undefined : onKeyDown}
            >
                <div className={classes.content}>
                    <span id={`${choice.elementId}_LABEL`}>{label}</span>
                </div>
            </div>
            <div className={classes.hiddenChoice} aria-hidden="true">
                <ChoiceContainer
                    {...getRendererProps(props)}
                    choiceRef={choiceRef}
                    choice={choice}
                    controlType={CarrouselScaleRadioAdapter}
                    openEnd={openEnd}
                    isFirst={isFirst}
                    isLast={isLast}
                    isDisabled={disabled}
                    isSelected={selected}
                    variable={variable}
                    gridRendering={gridRendering}
                    executeUpdateForIdAfterSelection={executeUpdateForIdAfterSelection}
                />
            </div>
            {hasOpenEnd(choice) && (
                <ChoiceOpenEndRenderer
                    {...getRendererProps(props)}
                    choice={choice}
                    isSelected={selected}
                    name={choice.elementId}
                    label={choiceLabel}
                    value={openEnd}
                    onOpenEndBlur={onOpenEndBlur}
                    onOpenEndChange={onOpenEndChange}
                    size={openEndSize}
                    lines={getSettingNumber(choice, "OpenEndLines", 2)}
                />
            )}
        </div>
    );
}
